import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Trash2, Upload, Eye, EyeOff } from "lucide-react";

const CATEGORIES = [
    { id: "pool-cage-screen-enclosure", label: "Pool cage / screen enclosure" },
    { id: "pool-rescreening",           label: "Pool rescreening" },
    { id: "screen-door",                label: "Screen door" },
    { id: "condo-screen",               label: "Condo screen" },
    { id: "lanai-patio",                label: "Lanai / patio" },
    { id: "other",                      label: "Other" },
];

const DEFAULT_TITLE = "A1 Screening Project Photo";
const DEFAULT_ALT = "A1 Screening pool cage and screen enclosure project photo";

function StorageBanner({ status, onRefresh }) {
    if (!status) return null;
    if (status.storage_active) {
        return (
            <div className="a1-card text-sm" data-testid="photos-storage-banner"
                 style={{ background: "rgba(40,216,224,0.06)", borderColor: "var(--color-accent)" }}>
                <strong style={{ color: "var(--color-accent)" }}>✓ Photo storage active.</strong>{" "}
                You can register photos with their hosted image URL (e.g. from Cloudinary).
            </div>
        );
    }
    return (
        <div className="a1-card text-sm" data-testid="photos-storage-banner"
             style={{ background: "rgba(202,138,4,0.08)", borderColor: "var(--color-warning)" }}>
            <strong style={{ color: "var(--color-warning)" }}>Photo upload is disabled.</strong>{" "}
            {status.message}
            <ul className="mt-2 ml-4 list-disc">
                {status.required_env_vars.map((v) => (
                    <li key={v}>
                        <code>{v}</code>
                        {status.missing_env_vars?.includes(v) ? (
                            <span className="ml-1 text-xs" style={{ color: "var(--color-warning)" }}>(missing)</span>
                        ) : (
                            <span className="ml-1 text-xs" style={{ color: "var(--color-success)" }}>(set)</span>
                        )}
                    </li>
                ))}
            </ul>
            <p className="mt-2 text-xs" style={{ color: "var(--color-text-muted)" }}>
                The existing static gallery on <code>/gallery</code> and the homepage are unaffected. They continue to render from the in-bundle photo set until upload storage is activated.
            </p>
            <button type="button" className="a1-btn-secondary text-xs px-3 py-1 mt-3"
                    onClick={onRefresh} data-testid="photos-storage-refresh">
                Re-check storage status
            </button>
        </div>
    );
}

function NewPhotoForm({ disabled, onCreated }) {
    const [form, setForm] = useState({
        title: "",
        alt_text: "",
        caption: "",
        category: "pool-cage-screen-enclosure",
        confirmed_service_type: "",
        confirmed_city: "",
        is_before_after: false,
        show_on_gallery: false,
        show_on_homepage: false,
        show_on_service_pages: false,
        sort_order: 0,
    });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [phase, setPhase] = useState(null); // 'signing' | 'uploading' | 'saving' | null
    const [err, setErr] = useState(null);
    const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const onPick = (e) => {
        setErr(null);
        const f = e.target.files?.[0] || null;
        if (f && f.size > 15 * 1024 * 1024) {
            setErr("Photo is larger than 15 MB. Please pick a smaller image.");
            setFile(null);
            return;
        }
        setFile(f);
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        if (!file) {
            setErr("Please choose a photo to upload.");
            return;
        }
        setBusy(true);
        try {
            // Step 1: mint signature server-side
            setPhase("signing");
            const sig = await api.get("/admin/photos/signature").then((r) => r.data);

            // Step 2: POST directly to Cloudinary
            setPhase("uploading");
            const fd = new FormData();
            fd.append("file", file);
            fd.append("api_key", sig.api_key);
            fd.append("timestamp", sig.timestamp);
            fd.append("signature", sig.signature);
            fd.append("folder", sig.folder);
            const cloudRes = await fetch(
                `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
                { method: "POST", body: fd },
            );
            if (!cloudRes.ok) {
                throw new Error(`Cloudinary returned HTTP ${cloudRes.status}`);
            }
            const cloud = await cloudRes.json();

            // Step 3: persist metadata server-side
            setPhase("saving");
            await api.post("/admin/photos", {
                image_url: cloud.secure_url,
                public_id: cloud.public_id,
                width: cloud.width,
                height: cloud.height,
                bytes: cloud.bytes,
                format: cloud.format,
                title: form.title.trim() || null,
                alt_text: form.alt_text.trim() || null,
                caption: form.caption.trim() || null,
                category: form.category,
                confirmed_service_type: form.confirmed_service_type.trim() || null,
                confirmed_city: form.confirmed_city.trim() || null,
                is_before_after: form.is_before_after,
                show_on_gallery: form.show_on_gallery,
                show_on_homepage: form.show_on_homepage,
                show_on_service_pages: form.show_on_service_pages,
                sort_order: Number(form.sort_order) || 0,
            });

            // Reset form
            setFile(null);
            setForm({
                ...form,
                title: "",
                alt_text: "",
                caption: "",
                confirmed_service_type: "",
                confirmed_city: "",
                is_before_after: false,
                show_on_gallery: false,
                show_on_homepage: false,
                show_on_service_pages: false,
            });
            onCreated();
        } catch (e2) {
            setErr(e2?.response?.data?.detail || e2?.message || "Could not upload photo.");
        } finally {
            setBusy(false);
            setPhase(null);
        }
    };

    return (
        <form onSubmit={submit} className="a1-card space-y-3" data-testid="admin-photo-new-form"
              style={{ opacity: disabled ? 0.45 : 1, pointerEvents: disabled ? "none" : "auto" }}>
            <div className="flex items-center gap-2">
                <span className="a1-icon-wrap-sm"><Upload size={14} /></span>
                <h3 className="text-lg m-0">Upload a new job photo</h3>
            </div>
            <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                Pick a photo from your phone or computer. It uploads directly to Cloudinary, then A1 Screening saves the title, category, and visibility settings here. Visibility toggles default to OFF — you choose where each photo appears.
            </p>

            <div>
                <label className="a1-label" htmlFor="admin-photo-file">Photo file</label>
                <input
                    id="admin-photo-file"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    onChange={onPick}
                    className="a1-input"
                    data-testid="admin-photo-input-file"
                    disabled={disabled}
                />
                {file ? (
                    <p className="a1-helper" data-testid="admin-photo-file-info">
                        {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                ) : null}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="a1-label">Title (optional)</label>
                    <input className="a1-input" value={form.title}
                           onChange={(e) => update("title", e.target.value)}
                           placeholder={DEFAULT_TITLE}
                           data-testid="admin-photo-input-title" disabled={disabled} />
                </div>
                <div>
                    <label className="a1-label">Alt text (optional)</label>
                    <input className="a1-input" value={form.alt_text}
                           onChange={(e) => update("alt_text", e.target.value)}
                           placeholder={DEFAULT_ALT}
                           data-testid="admin-photo-input-alt" disabled={disabled} />
                </div>
            </div>

            <div>
                <label className="a1-label">Caption (optional)</label>
                <input className="a1-input" value={form.caption}
                       onChange={(e) => update("caption", e.target.value)}
                       data-testid="admin-photo-input-caption" disabled={disabled} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                    <label className="a1-label">Category</label>
                    <select className="a1-input" value={form.category}
                            onChange={(e) => update("category", e.target.value)}
                            data-testid="admin-photo-input-category" disabled={disabled}>
                        {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="a1-label">Confirmed city (optional)</label>
                    <input className="a1-input" value={form.confirmed_city}
                           onChange={(e) => update("confirmed_city", e.target.value)}
                           placeholder="leave blank if not confirmed"
                           data-testid="admin-photo-input-city" disabled={disabled} />
                </div>
                <div>
                    <label className="a1-label">Confirmed service (optional)</label>
                    <input className="a1-input" value={form.confirmed_service_type}
                           onChange={(e) => update("confirmed_service_type", e.target.value)}
                           placeholder="leave blank if not confirmed"
                           data-testid="admin-photo-input-service" disabled={disabled} />
                </div>
            </div>

            <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2" data-testid="admin-photo-input-ba">
                    <input type="checkbox" checked={form.is_before_after}
                           onChange={(e) => update("is_before_after", e.target.checked)}
                           disabled={disabled} style={{ accentColor: "var(--color-accent)" }} />
                    Mark as before/after (only if a before photo exists)
                </label>
                <label className="flex items-center gap-2">
                    <input type="checkbox" checked={form.show_on_gallery}
                           onChange={(e) => update("show_on_gallery", e.target.checked)}
                           data-testid="admin-photo-input-show-gallery" disabled={disabled}
                           style={{ accentColor: "var(--color-accent)" }} />
                    Show on /gallery
                </label>
                <label className="flex items-center gap-2">
                    <input type="checkbox" checked={form.show_on_homepage}
                           onChange={(e) => update("show_on_homepage", e.target.checked)}
                           data-testid="admin-photo-input-show-home" disabled={disabled}
                           style={{ accentColor: "var(--color-accent)" }} />
                    Show on homepage teaser
                </label>
                <label className="flex items-center gap-2">
                    <input type="checkbox" checked={form.show_on_service_pages}
                           onChange={(e) => update("show_on_service_pages", e.target.checked)}
                           data-testid="admin-photo-input-show-service" disabled={disabled}
                           style={{ accentColor: "var(--color-accent)" }} />
                    Show on relevant service pages
                </label>
            </div>

            {err ? (
                <p className="text-sm" data-testid="admin-photo-error" style={{ color: "var(--color-danger)" }}>{err}</p>
            ) : null}

            <div>
                <button type="submit" className="a1-btn-primary" disabled={disabled || busy}
                        data-testid="admin-photo-submit-btn">
                    {busy
                        ? (phase === "signing" ? "Preparing…"
                           : phase === "uploading" ? "Uploading to Cloudinary…"
                           : "Saving…")
                        : "Upload photo"}
                </button>
            </div>
        </form>
    );
}

function PhotoRow({ row, onPatch, onDelete }) {
    const isLive =
        row.is_active && row.is_verified_real_job &&
        (row.show_on_gallery || row.show_on_homepage || row.show_on_service_pages);
    return (
        <tr className="border-b border-[var(--color-line)] align-top" data-testid={`admin-photo-row-${row.id}`}>
            <td className="py-3 pr-3 align-top">
                <img src={row.image_url} alt={row.alt} style={{ width: 120, height: 80, objectFit: "cover", borderRadius: 6, border: "1px solid var(--color-line)" }} />
                <div className="text-[10px] mt-1" style={{ color: "var(--color-text-dim)" }}>
                    {row.id.slice(0, 8)}…
                </div>
            </td>
            <td className="py-3 pr-3 align-top text-sm">
                <div className="font-semibold" style={{ color: "var(--color-text)" }}>{row.title}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>{row.caption || "—"}</div>
                <div className="text-[10px] mt-1 uppercase tracking-wider" style={{ color: "var(--color-text-dim)" }}>
                    {row.category}
                    {row.confirmed_city ? ` · ${row.confirmed_city}` : ""}
                    {row.confirmed_service_type ? ` · ${row.confirmed_service_type}` : ""}
                    {row.is_before_after ? " · before/after" : ""}
                </div>
            </td>
            <td className="py-3 pr-3 align-top">
                <div className="flex flex-col gap-1.5 text-xs">
                    {[
                        ["show_on_gallery", "Gallery"],
                        ["show_on_homepage", "Homepage"],
                        ["show_on_service_pages", "Service pages"],
                        ["is_verified_real_job", "Verified real"],
                        ["is_active", "Active"],
                    ].map(([k, label]) => (
                        <label key={k} className="flex items-center gap-2">
                            <input type="checkbox" checked={!!row[k]}
                                   onChange={(e) => onPatch(row.id, { [k]: e.target.checked })}
                                   data-testid={`admin-photo-toggle-${k}-${row.id}`}
                                   style={{ accentColor: "var(--color-accent)" }} />
                            {label}
                        </label>
                    ))}
                    <div className="mt-1 text-[10px] uppercase tracking-wider font-bold"
                         data-testid={`admin-photo-visibility-${row.id}`}
                         style={{ color: isLive ? "var(--color-success)" : "var(--color-warning)" }}>
                        {isLive ? <><Eye size={11} className="inline" /> Live</> : <><EyeOff size={11} className="inline" /> Hidden</>}
                    </div>
                </div>
            </td>
            <td className="py-3 align-top">
                <button type="button" className="a1-btn-secondary text-xs px-3 py-1"
                        style={{ borderColor: "var(--color-danger)", color: "var(--color-danger)" }}
                        onClick={() => { if (window.confirm("Delete this photo? This cannot be undone.")) onDelete(row.id); }}
                        data-testid={`admin-photo-delete-${row.id}`}>
                    <Trash2 size={12} className="inline" /> Delete
                </button>
            </td>
        </tr>
    );
}

export default function AdminPhotos() {
    const [status, setStatus] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [s, p] = await Promise.all([
                api.get("/admin/photos/upload-status"),
                api.get("/admin/photos"),
            ]);
            setStatus(s.data);
            setRows(p.data.photos || []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const onPatch = async (id, patch) => {
        await api.patch(`/admin/photos/${id}`, patch);
        await loadAll();
    };
    const onDelete = async (id) => {
        await api.delete(`/admin/photos/${id}`);
        await loadAll();
    };

    return (
        <div className="space-y-5" data-testid="admin-photos-page">
            <div className="flex flex-wrap items-baseline gap-4 justify-between">
                <h1 className="text-3xl m-0">Photos</h1>
                <div className="text-xs" style={{ color: "var(--color-text-dim)" }}>
                    {rows.length} admin photo{rows.length === 1 ? "" : "s"} on file
                </div>
            </div>

            <StorageBanner status={status} onRefresh={loadAll} />

            <NewPhotoForm
                disabled={!status?.storage_active}
                onCreated={loadAll}
            />

            <div className="a1-card overflow-x-auto p-0">
                <table className="w-full text-sm border-collapse" data-testid="admin-photos-table">
                    <thead>
                        <tr className="text-left border-b border-[var(--color-line)] uppercase tracking-wider text-xs"
                            style={{ color: "var(--color-text-dim)" }}>
                            <th className="py-3 px-3">Photo</th>
                            <th className="py-3 px-3">Metadata</th>
                            <th className="py-3 px-3">Visibility</th>
                            <th className="py-3 px-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="4" className="p-6 text-center" style={{ color: "var(--color-text-muted)" }}>Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan="4" className="p-6 text-center" style={{ color: "var(--color-text-muted)" }}>
                                No admin-uploaded photos yet. The existing static gallery on <code>/gallery</code> continues to render unchanged.
                            </td></tr>
                        ) : (
                            rows.map((r) => <PhotoRow key={r.id} row={r} onPatch={onPatch} onDelete={onDelete} />)
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
